"use client";

import { Loading01, Server01 } from "@untitledui/icons";
import { Tooltip } from "@/components/base/tooltip/tooltip";
import { TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { type BridgeStatus, useBridgeHealth } from "@/hooks/use-bridge-health";
import { cx } from "@/utils/cx";

interface BridgeHealthIndicatorProps {
    /** When provided, the indicator runs in controlled mode and uses these values instead of its own hook. */
    status?: BridgeStatus;
    lastTimestamp?: string | null;
    isRefreshing?: boolean;
    onRefresh?: () => void;
}

function formatTimestamp(isoString: string | null): string {
    if (!isoString) return "Never";
    try {
        return new Date(isoString).toLocaleString();
    } catch {
        return "Unknown";
    }
}

function getTooltipTitle(status: BridgeStatus): string {
    switch (status) {
        case "alive":
            return "Bridge Connected";
        case "dead":
            return "Bridge Disconnected";
        case "loading":
            return "Checking Bridge...";
        case "no-bridge":
            return "No bridge configured";
    }
}

function getTooltipDescription(status: BridgeStatus, lastTimestamp: string | null): string {
    switch (status) {
        case "alive":
            return `Last heartbeat: ${formatTimestamp(lastTimestamp)}`;
        case "dead":
            return lastTimestamp ? `Last heartbeat: ${formatTimestamp(lastTimestamp)}` : "No heartbeat found";
        case "loading":
            return "Waiting for first health check";
        case "no-bridge":
            return "This client has no bridge configured";
    }
}

export function BridgeHealthIndicator({
    status: statusProp,
    lastTimestamp: lastTimestampProp,
    isRefreshing: isRefreshingProp,
    onRefresh,
}: BridgeHealthIndicatorProps = {}) {
    // Run the hook in uncontrolled mode only when no external status is provided (e.g. AppLayout nav bar).
    const hook = useBridgeHealth(statusProp === undefined);

    const status = statusProp ?? hook.status;
    const lastTimestamp = lastTimestampProp ?? hook.lastTimestamp;
    const isRefreshing = isRefreshingProp ?? hook.isRefreshing;
    const refresh = onRefresh ?? hook.refresh;

    return (
        <Tooltip title={getTooltipTitle(status)} description={getTooltipDescription(status, lastTimestamp)} placement="bottom">
            <TooltipTrigger onPress={refresh}>
                <div
                    className={cx(
                        "relative flex size-7 cursor-pointer items-center justify-center rounded-full transition duration-100 ease-linear",
                        status === "alive" && "bg-utility-success-50 text-utility-success-600",
                        status === "dead" && "bg-utility-error-50 text-utility-error-600",
                        status === "loading" && "animate-pulse bg-secondary text-fg-quaternary",
                        status === "no-bridge" && "bg-secondary text-fg-quaternary",
                    )}
                    aria-label={getTooltipTitle(status)}
                >
                    <Server01 aria-hidden="true" className={cx("size-3.5 shrink-0 transition-opacity", isRefreshing && "opacity-0")} />
                    {isRefreshing && <Loading01 aria-hidden="true" className="absolute size-3.5 shrink-0 animate-spin" />}
                </div>
            </TooltipTrigger>
        </Tooltip>
    );
}
